import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/useAppStore';
import { usePlayerV2 } from '../stores/usePlayerV2';
import { VIBE_LABELS } from '../types';
import './PlayerRoute.css';

function fmtTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const SKIP_SEC = 10;

export default function PlayerRoute() {
  const status = usePlayerV2((s) => s.status);
  const storyId = usePlayerV2((s) => s.storyId);
  const title = usePlayerV2((s) => s.title);
  const pos = usePlayerV2((s) => s.positionSec);
  const dur = usePlayerV2((s) => s.durationSec);
  const fp = usePlayerV2((s) => s.fetchProgress);
  const error = usePlayerV2((s) => s.error);
  const nextStoryId = usePlayerV2((s) => s.nextStoryId);
  const pause = usePlayerV2((s) => s.pause);
  const resume = usePlayerV2((s) => s.resume);
  const stop = usePlayerV2((s) => s.stop);
  const seek = usePlayerV2((s) => s.seek);
  const skipNext = usePlayerV2((s) => s.skipNext);

  const stories = useAppStore((s) => s.stories);
  const universes = useAppStore((s) => s.universes);
  const setActiveUniverse = useAppStore((s) => s.setActiveUniverse);
  const setActiveStoryForUniverse = useAppStore(
    (s) => s.setActiveStoryForUniverse
  );
  const nav = useNavigate();

  if (status === 'idle' || !storyId) {
    return (
      <div className="route player-route player-route-empty">
        <div className="dim">Nothing playing.</div>
        <button className="primary" onClick={() => nav('/library')}>
          Open library
        </button>
      </div>
    );
  }

  const story = stories[storyId];
  const universe = story ? universes[story.universeId] : undefined;
  const hook = story?.slug.hook;
  const vibe = story?.slug.vibe;
  const nextStory = nextStoryId ? stories[nextStoryId] : undefined;

  const preplay =
    status === 'resolving' || status === 'fetching' || status === 'buffering';
  const playing = status === 'playing';

  let timeLine: string;
  if (status === 'error') timeLine = error ?? 'Error';
  else if (status === 'resolving') timeLine = 'Loading…';
  else if (status === 'fetching') {
    if (fp && fp.total) timeLine = `${fmtBytes(fp.loaded)} / ${fmtBytes(fp.total)}`;
    else if (fp) timeLine = `${fmtBytes(fp.loaded)} loaded`;
    else timeLine = 'Downloading…';
  } else if (status === 'buffering' && pos === 0) timeLine = 'Buffering…';
  else if (status === 'ended') timeLine = `${fmtTime(dur)} / ${fmtTime(dur)}`;
  else timeLine = `${fmtTime(pos)} / ${fmtTime(dur)}`;

  return (
    <div className="route player-route">
      <div className="player-meta">
        {universe && <div className="player-universe">{universe.name}</div>}
        <h1 className="player-title">{title}</h1>
        {vibe && (
          <span className="tag tag-soft vibe-tag">{VIBE_LABELS[vibe]}</span>
        )}
        {hook && <p className="player-hook">{hook}</p>}
      </div>

      <div className={`player-time ${status === 'error' ? 'error' : ''}`}>
        {timeLine}
      </div>

      <div className="player-controls">
        <button
          className="player-skip"
          onClick={() => seek(Math.max(0, pos - SKIP_SEC))}
          disabled={preplay || status === 'ended' || status === 'error'}
          aria-label="Back 10 seconds"
        >
          −10s
        </button>
        <button
          className="player-toggle"
          onClick={() => {
            if (playing) pause();
            else void resume();
          }}
          disabled={preplay || status === 'error'}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {preplay ? (
            <span className="player-spinner" aria-hidden="true" />
          ) : playing ? (
            <span aria-hidden="true">❚❚</span>
          ) : (
            <span aria-hidden="true">▶</span>
          )}
        </button>
        <button
          className="player-skip"
          onClick={() => seek(Math.min(dur || 0, pos + SKIP_SEC))}
          disabled={preplay || status === 'ended' || status === 'error'}
          aria-label="Forward 10 seconds"
        >
          +10s
        </button>
      </div>

      <div className="player-secondary">
        <button
          className="ghost"
          onClick={skipNext}
          disabled={!nextStoryId}
        >
          Skip ▶▶
        </button>
        <button
          className="ghost"
          onClick={() => {
            if (!story) return;
            setActiveUniverse(story.universeId);
            setActiveStoryForUniverse(story.universeId, storyId);
            nav('/workspace');
          }}
          disabled={!story}
        >
          View
        </button>
        <button className="ghost" onClick={stop}>
          Stop
        </button>
      </div>

      {nextStory && (
        <div className="player-upnext">
          <div className="dim small">Up next</div>
          <div className="player-upnext-title">{nextStory.slug.title}</div>
        </div>
      )}
    </div>
  );
}
